import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useUserContext } from "../../../context/UserProvider";
import { getLocalTodayKey, groupChallengesByStatus } from "./challengePageUtils";
import useChallengeProgress from "./useChallengeProgress";
import "./ChallengesPage.css";

const numberFormat = new Intl.NumberFormat("en-CA", { maximumFractionDigits: 1 });
const formatKm = (distance) => `${numberFormat.format(distance)} km`;
const getTarget = (race) => {
  const distance = Number(race.raceInfo?.distance);
  return Number.isFinite(distance) && distance > 0 ? distance : null;
};

function Icon({ name, ...props }) {
  const paths = {
    arrow: <path d="m10 6-6 6 6 6M4 12h16" />,
    calendar: <><rect x="4" y="5" width="16" height="16" rx="3" /><path d="M8 3v4m8-4v4M4 11h16" /></>,
    chart: <><path d="M5 20V10h4v10m2 0V4h4v16m2 0V7h4v13" /></>,
    trophy: <><path d="M8 4h8v5a4 4 0 0 1-8 0V4Zm4 9v5m-4 3h8M8 6H4v2a4 4 0 0 0 4 4m8-6h4v2a4 4 0 0 1-4 4" /></>,
    check: <path d="m5 12 4 4L19 6" />,
    chevron: <path d="m6 9 6 6 6-6" />,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  };
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>{paths[name]}</svg>;
}

function ChallengeLogo({ race }) {
  const [failedUrl, setFailedUrl] = useState(null);
  const url = race.raceInfo?.imageUrl;
  return <div className="challenge-logo">
    {url && failedUrl !== url
      ? <img src={url} alt={`${race.raceInfo?.name || "Challenge"} logo`} onError={() => setFailedUrl(url)} />
      : <Icon name="trophy" />}
  </div>;
}

function Dates({ race }) {
  const localDatePolicy = race.raceInfo?.activityDatePolicy === "participant_local_v1";
  const format = (value) => {
    const date = new Date(value);
    return Number.isFinite(date.getTime()) ? date.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric", ...(typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) ? { timeZone: "UTC" } : {}) }) : null;
  };
  const start = format(localDatePolicy ? race.raceInfo?.activityStartDate : race.raceInfo?.raceDateStart);
  const end = format(localDatePolicy ? race.raceInfo?.activityEndDate : race.raceInfo?.raceDateEnd);
  return <span className="challenge-dates"><Icon name="calendar" />{start && end ? `${start} – ${end}` : "Dates to be confirmed"}</span>;
}

function ChallengeLinks({ race, featured = false }) {
  const id = race.legId.replace(/^price_/, "");
  return <div className={`challenge-actions${featured ? " challenge-actions-featured" : ""}`}>
    <Link to={`/dashboard/${id}`}><Icon name="chart" />{featured ? "My Dashboard" : "Dashboard"}</Link>
    <Link to={`/leaderboard/${id}`}><Icon name="trophy" />{featured ? "View Leaderboard" : "Leaderboard"}</Link>
  </div>;
}

function CurrentChallenge({ race, userId, now, timeZone }) {
  const progress = useChallengeProgress(race, userId);
  const target = getTarget(race);
  const localDatePolicy = race.raceInfo?.activityDatePolicy === "participant_local_v1";
  const start = localDatePolicy
    ? Date.parse(`${race.raceInfo?.activityStartDate}T12:00:00Z`)
    : new Date(race.raceInfo?.raceDateStart).getTime();
  const end = localDatePolicy
    ? Date.parse(`${race.raceInfo?.activityEndDate}T12:00:00Z`)
    : new Date(race.raceInfo?.raceDateEnd).getTime();
  const todayKey = getLocalTodayKey(now, timeZone);
  const upcoming = localDatePolicy
    ? todayKey < race.raceInfo?.activityStartDate
    : Number.isFinite(start) && start > now;
  const days = localDatePolicy
    ? Math.max(0, Math.round(((upcoming ? start : end) - Date.parse(`${todayKey}T12:00:00Z`)) / 86400000))
    : Math.max(0, Math.ceil(((upcoming ? start : end) - now) / 86400000));
  const timeLabel = Number.isFinite(days)
    ? upcoming ? `Starts in ${days} ${days === 1 ? "day" : "days"}` : days === 0 ? "Ends today" : `${days} ${days === 1 ? "day" : "days"} left`
    : null;
  const percent = progress.status === "ready" && target ? Math.min(100, progress.distance / target * 100) : null;
  const remaining = target && progress.status === "ready" ? Math.max(0, target - progress.distance) : null;

  return <article className="challenge-current-card" aria-label={race.raceInfo?.name}>
    <div className="challenge-featured-logo">
      <ChallengeLogo race={race} />
      <span className={`challenge-badge ${upcoming ? "challenge-badge-upcoming" : "challenge-badge-active"}`}><span className="challenge-dot" />{upcoming ? "Upcoming" : "In progress"}</span>
    </div>
    <div className="challenge-current-body">
      <div className="challenge-timing"><Dates race={race} />{timeLabel && <span className="challenge-countdown">{timeLabel}</span>}</div>
      <h3>{race.raceInfo?.name || "Your challenge"}</h3>
      {race.raceInfo?.legName && <p className="challenge-leg">{race.raceInfo.legName}</p>}
      {progress.isTeam && <p className="challenge-team">Team: {race.teamName}</p>}
      <div className="challenge-progress" aria-live="polite">
        {progress.status === "loading" ? <p className="challenge-progress-message">Loading live progress…</p>
          : progress.status === "error" ? <p className="challenge-progress-message">Progress is currently unavailable. Please try again later.</p>
          : <>
            <div className="challenge-progress-label">
              <span>{progress.isTeam ? "Team distance" : "Distance logged"}: <strong>{formatKm(progress.distance)}</strong>{target && <span className="challenge-target"> / {formatKm(target)} target</span>}</span>
              {percent !== null && <span className="challenge-percent">{Math.floor(percent)}% complete</span>}
            </div>
            {percent !== null && <>
              <div className="challenge-progress-track" role="progressbar" aria-label={`${race.raceInfo?.name} ${progress.isTeam ? "team" : "personal"} progress`} aria-valuenow={Math.floor(percent)} aria-valuemin={0} aria-valuemax={100}>
                <div style={{ width: `${percent}%` }} />
              </div>
              <div className="challenge-progress-scale"><span>Start</span><span>{remaining === 0 ? "Distance goal reached" : `${formatKm(remaining)} to your goal`}</span><span>{formatKm(target)}</span></div>
            </>}
          </>}
      </div>
    </div>
    <ChallengeLinks race={race} featured />
  </article>;
}

function FinishedChallenge({ race }) {
  const target = getTarget(race);
  return <article className="challenge-finished-card">
    <div className="challenge-card-content">
      <div className="challenge-card-top"><ChallengeLogo race={race} /><span className="challenge-badge challenge-badge-active"><Icon name="check" />Finished</span></div>
      <h3>{race.raceInfo?.name || "Your challenge"}</h3>
      <Dates race={race} />
      {race.raceInfo?.legName && <p className="challenge-leg">{race.raceInfo.legName}</p>}
      {target && <div className="challenge-detail"><span>Challenge distance</span><strong>{formatKm(target)}</strong></div>}
    </div>
    <ChallengeLinks race={race} />
  </article>;
}

function ConcludedChallenge({ race }) {
  const target = getTarget(race);
  return <article className="challenge-concluded-card">
    <div className="challenge-archive-info"><ChallengeLogo race={race} /><div><h3>{race.raceInfo?.name || "Your challenge"}</h3><Dates race={race} />{race.raceInfo?.legName && <p className="challenge-leg">{race.raceInfo.legName}</p>}{target && <span className="challenge-archive-distance">{formatKm(target)}</span>}</div></div>
    <ChallengeLinks race={race} />
  </article>;
}

export default function ChallengesPage() {
  const { userRaces, user, userData } = useUserContext();
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, []);
  const groups = groupChallengesByStatus(userRaces, now, userData?.timeZone || "");
  const total = groups.current.length + groups.finished.length + groups.concluded.length;

  return <main className="challenges-page"><div className="challenges-page-inner">
    <div className="challenges-page-heading">
      <div><span className="challenges-eyebrow">Your journey</span><h1>Your Challenges</h1><p>Every kilometre. Every milestone.</p></div>
      <Link className="challenge-home-link" to="/"><Icon name="arrow" />Back to home</Link>
    </div>
    {total === 0 ? <div className="challenges-empty"><Icon name="trophy" /><p>You don’t have any challenges yet.</p></div> : <>
      {groups.current.length > 0 && <section className="challenges-current-section" aria-labelledby="current-challenges-heading">
        <div className="challenge-section-heading"><h2 id="current-challenges-heading"><span className="challenge-dot" />Current Challenges</h2><span>{groups.current.length} {groups.current.length === 1 ? "challenge" : "challenges"}</span></div>
        <div className="challenge-current-list">{groups.current.map((race) => <CurrentChallenge key={`${user?.uid}:${race.legId}`} race={race} userId={user?.uid} now={now} timeZone={userData?.timeZone} />)}</div>
      </section>}
      {groups.finished.length > 0 && <section className="challenges-finished-section" aria-labelledby="finished-challenges-heading">
        <div className="challenge-section-heading"><div><h2 id="finished-challenges-heading">Finished Challenges</h2><p>A look back at what you’ve accomplished.</p></div><span>{groups.finished.length} completed</span></div>
        <div className="challenge-finished-grid">{groups.finished.map((race) => <FinishedChallenge key={race.legId} race={race} />)}</div>
      </section>}
      {groups.concluded.length > 0 && <section className="challenges-archive-section" aria-labelledby="concluded-challenges-heading">
        <h2 id="concluded-challenges-heading"><button type="button" className="challenge-archive-toggle" aria-expanded={archiveOpen} aria-controls="concluded-challenges" onClick={() => setArchiveOpen(!archiveOpen)}><span><Icon name="chevron" /><span>Concluded Challenges</span><span className="challenge-archive-count">{groups.concluded.length}</span></span><span className="challenge-archive-action">{archiveOpen ? "Hide" : "Show"} archive</span></button></h2>
        <div id="concluded-challenges" hidden={!archiveOpen}>{archiveOpen && <div className="challenge-archive-grid">{groups.concluded.map((race) => <ConcludedChallenge key={race.legId} race={race} />)}</div>}</div>
      </section>}
    </>}
  </div></main>;
}
